import { AlertTriangleIcon, InfoIcon } from 'lucide-react';
import type { JSX } from 'react';

import type {
  Run,
  WorkspaceKnowledgeRunStatus,
} from '@/lib/api/quality-types.js';

import { cn } from '@/lib/utils.js';
import { runLifecycleStatus } from './quality-outcome.js';

type WorkspaceKnowledgeRun = Pick<
  Run,
  | 'status'
  | 'workspace_knowledge'
  | 'outcome_protocol_version'
  | 'execution_status'
>;

const KNOWN_STATUSES = new Set<WorkspaceKnowledgeRunStatus>([
  'pending',
  'ready',
  'empty',
  'unavailable',
  'forbidden',
  'malformed',
  'oversized',
  'persistence_failure',
]);

function copyForStatus(
  status: WorkspaceKnowledgeRunStatus,
  runStatus: ReturnType<typeof runLifecycleStatus>,
): { role: 'status' | 'alert'; copy: string } {
  if (status === 'pending') {
    return {
      role: 'status',
      copy: 'Workspace knowledge is still being prepared for this run.',
    };
  }
  if (status === 'ready') {
    return {
      role: 'status',
      copy: 'Workspace knowledge evidence is frozen for this run.',
    };
  }
  if (
    status === 'empty' &&
    (runStatus === 'failed' || runStatus === 'not_completed')
  ) {
    return {
      role: 'alert',
      copy: 'Workspace knowledge returned no usable evidence; this generation run failed. Review the run details below.',
    };
  }
  if (status === 'empty') {
    return {
      role: 'alert',
      copy: 'Workspace knowledge returned no usable evidence. The run continued with the other selected sources.',
    };
  }
  if (status === 'unavailable') {
    return {
      role: 'alert',
      copy: 'Workspace knowledge was unavailable. The run continued with the other selected sources.',
    };
  }
  if (status === 'forbidden') {
    return {
      role: 'alert',
      copy: 'Workspace access changed during the run, so Workspace knowledge was not used.',
    };
  }
  if (status === 'malformed') {
    return {
      role: 'alert',
      copy: 'Workspace knowledge returned an invalid response, so the affected evidence was not used.',
    };
  }
  if (status === 'oversized') {
    return {
      role: 'alert',
      copy: 'Workspace knowledge returned an oversized response, so the affected evidence was not used.',
    };
  }
  if (status === 'persistence_failure') {
    return {
      role: 'alert',
      copy: 'Workspace knowledge evidence could not be saved, so the affected evidence was not used.',
    };
  }
  return {
    role: 'alert',
    copy: 'Workspace knowledge had a retrieval issue. Review the run details below.',
  };
}

export function WorkspaceKnowledgeRunNotice({
  run,
}: {
  run: WorkspaceKnowledgeRun;
}): JSX.Element | null {
  const state = run.workspace_knowledge;
  if (!state?.enabled) return null;
  const knownStatus = KNOWN_STATUSES.has(state.status);
  const runStatus = runLifecycleStatus(run);
  const resolved = copyForStatus(
    knownStatus ? state.status : 'unknown',
    runStatus,
  );
  const durableMessage =
    typeof state.message === 'string' ? state.message.trim() : '';
  const role =
    durableMessage && state.status === 'ready' ? 'alert' : resolved.role;
  const copy = durableMessage || resolved.copy;
  const Icon = role === 'alert' ? AlertTriangleIcon : InfoIcon;

  return (
    <div
      data-testid="workspace-knowledge-run-state"
      role={role}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-xs',
        role === 'alert'
          ? 'border-warning-500/40 bg-warning-500/10 text-warning-600'
          : 'border-border bg-muted/30 text-muted-foreground',
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>{copy}</p>
    </div>
  );
}
